import moment from "moment";

import { EmailContact } from "@/Common/Api/Contacts/Dto/EmailContact";
import { PatientApiClient } from "@/Common/Api/Patient/PatientApiClient";
import { SignatureClient } from "@/Common/Api/Signature/SignatureClient";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { InformationLoadingComponent } from "@/Common/Components/Loader/InformationLoadingComponent";
import { MultipleComponentPopup } from "@/Common/Components/Modals/MultipleComponentPopup";
import { InvitationContact, SendInvitationRequest } from "@/Common/Components/Portal/InvitationPopup/Api/Dto/SendInvitationRequest";
import { sendInvitation } from "@/Common/Components/Portal/InvitationPopup/Api/InvitationApiClient";
import { IInvitationContactEmail, IInvitationContactPhone } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/AddContactForm/AddContactForm";
import { ContactForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/ContactForm/ContactForm";
import { MessageForm } from "@/Common/Components/Portal/InvitationPopup/InvitationForm/MessageForm/MessageForm";
import { PortalStatusIcon } from "@/Common/Components/Portal/PortalStatusIcon";
import { withPortalCommunicationMessageForm, WithPortalCommunicationMessageFormProps } from "@/Common/Components/SharedItem/hoc/withPortalCommunicationMessageForm";
import { usePortalCommunicationMessageForm } from "@/Common/Components/SharedItem/Hooks/usePortalCommunicationMessageForm";
import { PortalCommunicationMessageForm } from "@/Common/Components/SharedItem/PortalCommunicationMessageForm";
import { SignaturePinControl } from "@/Common/Components/Sign/SignaturePinControl";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalRegistrationState } from "@/Common/Models/Portal/Enums/PortalRegistrationState";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { GetPatientOverviewResponse } from "@/Common/Models/Portal/MedfarPortal";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemFile } from "@/Common/Models/Portal/SharedItem/SharedItemFile";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { CustomFaxClient } from "@/ExportableFile/Api/CustomFaxClient";
import { FilePreviewComponentEditable, FilePreviewComponentMode } from "@/ExportableFile/Components/FileSelection/FilePreviewComponentEditable";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFilePopupSettingModel } from "@/ExportableFile/Models/ExportableFilePopupSettingModel";
import { ExportableFileSignatureSettingsModel } from "@/ExportableFile/Models/ExportableFileSignatureSettingsModel";
import { FileTypeAttachment } from "@/ExportableFile/Models/FileTypes/FileTypeAttachment";
import { FileTypeRadPdf } from "@/ExportableFile/Models/FileTypes/FileTypeRadPdf";
import { PrepositionedSignatureOverlayElement } from "@/ExportableFile/Models/OverlayElements/PrepositionedSignatureOverlayElement";
import { sharedItemProviderFactory } from "@/Portal/Services/SharedItemProviderFactory";
import { ISendToPortalAndPrintPopupStrategy, ISendToPortalPopupStrategy, ISendToPortalWithPreviewPopupStrategy, ISignAndSendToPortalPopupStrategy } from "@/Portal/Services/Strategies/ISendToPortalPopupStrategy";

import "./SendToPortalPopup.css";

interface SendToPortalPopupProps {
    model: IPortalLinkedItemModel;
    strategy: ISendToPortalPopupStrategy;
    largePreview?: boolean;
    isAsyncCallback?: (isSendSuccessed: boolean) => void;
    onClosePopup?: () => void;
}

class EmployeeFormSign {
    public isFirstTime: boolean;
    public shouldSign: boolean;
}

class SendToPortalPopupState {
    public isClickableTextPopupOpen: boolean;
    public showSubjectReadOnly: boolean;
    public includeLinkedItem: boolean;
    public allContacts: { emails: IInvitationContactEmail[], phones: IInvitationContactPhone[] };
    public email: IInvitationContactEmail;
    public phone: IInvitationContactPhone;
    public subjectText: string;
    public newMessageText: string;
    public selectedPreviewFile: SharedItemFile;
    public previewFileCachebust: number;
    public waitingForPortalOverview: boolean;
    public portalOverview: GetPatientOverviewResponse;
    public isPinValid: boolean;
    public defaultFilePreviewComponentMode = FilePreviewComponentMode.iFramePreview;
    public currentPreviewMode = FilePreviewComponentMode.iFramePreview;
    public linkedShareMessages: Immutable.List<SharedItemMessage>;
    public displayPinComponent: boolean;
    public hideButtonsPanel: boolean;
    public employeeFormSign: EmployeeFormSign;
    public exportableFilePopupSetting: ExportableFilePopupSettingModel;
}

type CombinedPortalStatusProps = SendToPortalPopupProps &
ReturnType<typeof usePortalCommunicationMessageForm> &
WithPortalCommunicationMessageFormProps;

class SendToPortalPopup extends BaseReactComponent<CombinedPortalStatusProps, SendToPortalPopupState> {
    private filePreviewComponentEditable: FilePreviewComponentEditable;

    private readonly isPortalInvitationFlowV2Enabled: boolean = cacheStore.ClinicSettings.EnablePortalInvitationFlowV2;
    private readonly isBidirectionalCommunicationEnabled: boolean = cacheStore.ClinicSettings.EnableBidirectionalCommunication;

    private get shouldShowInvitation(): boolean {
        const portalRegistrationStatus = this.props.model.get("RegistrationStatus");

        if (!portalRegistrationStatus) {
            return true;
        }

        return portalRegistrationStatus !== PortalRegistrationState.RegistrationComplete &&
            portalRegistrationStatus !== PortalRegistrationState.RegistrationPending &&
            portalRegistrationStatus !== PortalRegistrationState.PortalRefused &&
            portalRegistrationStatus !== PortalRegistrationState.ChildAccount;
    }

    private readonly componentId = "sendToPortalPopup";

    public componentDidMount(): void {
        this.initializeComponent();
    }

    public async initializeComponent(): Promise<void> {
        const registrationStatus = this.props.model.get("RegistrationStatus");

        const needsPortalOverview = registrationStatus === PortalRegistrationState.RegistrationPending
            || registrationStatus === PortalRegistrationState.ChildAccount;

        const includeLinkedItem = this.getDefaultValueForIncludeLinkItem();
        const subject = this.getDefaultSubject();

        const sendWithPreviewStrategy = this.props.strategy as ISendToPortalWithPreviewPopupStrategy;
        const shouldShowFilePreview = sendWithPreviewStrategy.shouldShowFilePreview;

        const sharedItem = this.props.model.get("SharedItem");

        const patient = await this.loadPatient();
        const contacts = this.getPatientContacts(patient);

        let isPinValid = false;
        let employeeFormSignResponse: EmployeeFormSign = { isFirstTime: true, shouldSign: false };
        const userSettingPreferences = ExportableFilePopupSettingModel.fromCacheModel(cacheStore.UserSettings.ExportableFilePopup);
        const exportableFileType = this.getExportableFileType();

        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
            const pinFrequencyModel = LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency);
            const pinFrequencyToken = sharedItemProviderFactory.get(this.props.strategy.linkedItemType).getPinFrequencyToken(pinFrequencyModel);
            isPinValid = await ExportableFileHelper.validatePinFrequencyToken(pinFrequencyToken);

            if (this.props.strategy.linkedItemType == PortalLinkedItemType.MedicalNoteForm)
                employeeFormSignResponse = await this.loadRadPdfEmployeeFormSign();
            else if (!!userSettingPreferences && !!exportableFileType) {
                const setting = userSettingPreferences[exportableFileType]?.exportableFileSignatureSettings?.firstOrDefault(
                    p => p.actionType == ExportMode.SendToPortal
                );

                if (setting?.shouldSign) {
                    employeeFormSignResponse.shouldSign = setting.shouldSign;
                    employeeFormSignResponse.isFirstTime = !setting.shouldSign;
                }
            }
        }

        this.mutateState(
            s => {
                s.isPinValid = isPinValid;
                s.allContacts = contacts;
                s.email = this.shouldShowInvitation ? s.allContacts?.emails[0] : null;
                s.subjectText = subject;
                s.newMessageText = this.props.model.get("DefaultBody");
                s.includeLinkedItem = includeLinkedItem;
                s.isClickableTextPopupOpen = false;
                s.waitingForPortalOverview = needsPortalOverview;
                s.portalOverview = null;
                s.linkedShareMessages = this.props.model.get("LinkedItemMessages");
                s.showSubjectReadOnly = sharedItem
                    && sharedItem.Status !== PortalSyncStatus.Revoked
                    && sharedItem.Status !== PortalSyncStatus.Unsent
                    && this.props.strategy.linkedItemType !== PortalLinkedItemType.CommunicationHistory;

                if (sharedItem?.Files?.length && shouldShowFilePreview) {
                    s.selectedPreviewFile = sharedItem.Files[0];
                    s.previewFileCachebust = Date.now();
                }

                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                    s.employeeFormSign = employeeFormSignResponse;
                    s.exportableFilePopupSetting = userSettingPreferences ?? new ExportableFilePopupSettingModel();

                    if (!isPinValid && !employeeFormSignResponse.isFirstTime && employeeFormSignResponse.shouldSign)
                        s.displayPinComponent = true;
                }
            },
            () => {
                if (!needsPortalOverview)
                    return;

                patientStore.getPortalPatientOverview(
                    this.props.model.get("PatientContactId"),
                    overview => {
                        this.mutateState(
                            s => {
                                s.portalOverview = overview;
                                s.waitingForPortalOverview = false;
                            }
                        );
                    }
                );
            }
        );

        super.setOverlay("sendToPortalPopup", { transparent: false });

        if (sharedItem) {
            this.props.onSetFormState?.({
                canPatientReplyWithMessage: sharedItem.CanPatientReplyWithMessage
            });
        }
    }

    public render(): JSX.Element {
        // Since the state is being initialized during ComponentDidMount we should prevent the render until the method has finished
        // LinkedItemModel is being loaded asynchronously
        // do not render if not loaded
        if (!this.state || !this.props.model.get("LinkedItemModel"))
            return null;

        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const sendWithPreviewStrategy = this.props.strategy as ISendToPortalWithPreviewPopupStrategy;

        const showFilePreview = sendWithPreviewStrategy.shouldShowFilePreview;
        const planActionsItemType = this.props.strategy.linkedItemType === PortalLinkedItemType.LabRequest || this.props.strategy.linkedItemType === PortalLinkedItemType.ConsultationRequest;

        return (
            <MultipleComponentPopup
                onCloseCallback={() => this.props.strategy.onClosePopupClick()}
                isBackgroundGrey
                contentJustification="center"
                contentMarginTop={-30}
            >
                <div className="popup sendtoportal-popup">
                    <div className="row p-header row-margin">
                        <div className="col-lg-12  col-md-12 col-sm-12">
                            <a className="close" aria-label="Close" onClick={() => this.props.onClosePopup ? this.props.onClosePopup() : this.props.strategy.onClosePopupClick()}> x</a>
                            <h3 className="header thin-font">
                                {cacheStore.GetString("SendToPatientPortal")}
                            </h3>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            {this.state.waitingForPortalOverview
                                ? <InformationLoadingComponent smallVersion={true} customMessage={cacheStore.GetString("LoadPortalInfoLabel")} />
                                : this.renderPopup(showFilePreview)
                            }
                        </div>
                    </div>
                    {!this.state.waitingForPortalOverview && (
                        <div className="row p-footer row-margin">
                            <div className="flex-container">
                                <div className="inline-block footer-options" style={{ paddingLeft: "10px" }}>
                                    {signAndSendStrategy.sharedItemCanBeSigned && !this.state.hideButtonsPanel && (!planActionsItemType || !this.getSignedPrintAuthorizations()) && (
                                        <>
                                            {((!cacheStore.ClinicSettings.EnableQuickDocumentSigning || this.state.displayPinComponent)) && (
                                                <SignaturePinControl
                                                    disableControl={!this.state.includeLinkedItem}
                                                    onSuccessFunction={pin => this.applySignatureOnPinSuccess(pin)}
                                                    onRejectFunction={() => this.onPinReject()}
                                                />
                                            )}
                                            {cacheStore.ClinicSettings.EnableQuickDocumentSigning && !this.state.displayPinComponent && (
                                                this.renderQuickSignatureButtons()
                                            )}
                                        </>
                                    )}
                                </div>
                                {this.renderButtons()}
                            </div>
                        </div>
                    )}
                </div>
            </MultipleComponentPopup>
        );
    }

    private renderQuickSignatureButtons(): JSX.Element {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const exportableFile = signAndSendStrategy.signableFile as ExportableFile;
        const signableFileIsExportable = signAndSendStrategy.signableFile instanceof ExportableFile;

        const showRemoveSignatureButton =
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && this.state.isPinValid
            && this.hasSignatureAccordingToPlanActionsPreferences();

        if (this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit && this.state.isPinValid && signableFileIsExportable) {
            return (
                <div className="sent-to-portal-footer">
                    <ButtonControl
                        className="btn btn-primary"
                        label={cacheStore.GetString("DocumentSigningPinOptions_AddSignatureLabel")}
                        disabled={!this.state.includeLinkedItem}
                        onClick={() => {
                            this.filePreviewComponentEditable?.onSignButton();
                            this.mutateState(s => s.employeeFormSign.shouldSign = true);
                        }}
                    />
                    <ButtonControl
                        className="btn btn-default"
                        label={cacheStore.GetString("PreviewLabel")}
                        disabled={!this.state.includeLinkedItem}
                        onClick={() => {
                            this.filePreviewComponentEditable?.applyOverlaysAndSwitchToPreviewMode();
                            this.mutateState(s => {
                                s.currentPreviewMode = FilePreviewComponentMode.iFramePreview;
                                s.defaultFilePreviewComponentMode = FilePreviewComponentMode.iFramePreview;
                            });
                        }}
                    />
                </div>
            );
        }

        return (
            <>
                {!showRemoveSignatureButton && (
                    <ButtonControl
                        className="btn btn-default"
                        disabled={!this.state.includeLinkedItem}
                        label={this.hasBeenSigned() ? cacheStore.GetString("EDITLabel") : cacheStore.GetString("SignLabel")}
                        onClick={() => this.onSignButtonClick()}
                    />
                )}

                {showRemoveSignatureButton && (
                    <ButtonControl
                        className="btn btn-default"
                        label={cacheStore.GetString("DocumentSigningPinOptions_DeleteSignatureLabel")}
                        disabled={exportableFile?.overlayElements?.length < 0 || !this.state.includeLinkedItem}
                        onClick={() => { this.onRemoveSignatureButtonClick(); }}
                    />
                )}
            </>
        );
    }

    private handleSettingPinAndSignature(): void {
        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.DisplaySignatureOptionPopup,
            {
                onSaveSuccess: () => {
                    if (this.state.isPinValid && sessionStore.signOnPrintAccess)
                        this.applySignatureOnPinSuccess();
                }
            }
        );
    }

    private async loadRadPdfEmployeeFormSign(): Promise<{ isFirstTime: boolean, shouldSign: boolean }> {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const signableFile = signAndSendStrategy.signableFile;
        const formId = (signableFile as FileTypeRadPdf)?.formId;
        const actionType = ExportMode.SendToPortal;

        if (formId) {
            const response = await CustomFaxClient.loadEmployeeFormSign(
                sessionStore.getCurrentEmployee()?.ContactId,
                formId,
                actionType
            );

            if (response) {
                return { isFirstTime: !response.HasRecord, shouldSign: response.ShouldSign };
            }
        }

        return { isFirstTime: true, shouldSign: false };
    }

    private onSignButtonClick(): void {
        this.mutateState(p => {
            if (p.isPinValid && sessionStore.signOnPrintAccess)
                this.applySignatureOnPinSuccess();
            else if (sessionStore.signOnPrintAccess)
                this.mutateState(s => s.displayPinComponent = true);
            else
                this.handleSettingPinAndSignature();
        });
    }

    private renderPopup(showFilePreview: boolean): JSX.Element {
        let popupClass = "generic-portal-popup";

        if (showFilePreview) {
            popupClass += " generic-portal-preview-popup row-body";

            if (this.props.largePreview) {
                popupClass += " large-preview";
            }
        }

        let warningMsg: string = null;
        const portalRegistrationStatus = this.props.model.get("RegistrationStatus");

        if (portalRegistrationStatus !== PortalRegistrationState.RegistrationComplete) {
            const isChildWarning = PortalHelper.hasGrantedAccessToOtherProfiles(this.state.portalOverview)
                && (
                    portalRegistrationStatus == PortalRegistrationState.ChildAccount
                    || portalRegistrationStatus == PortalRegistrationState.RegistrationPending
                );

            warningMsg = cacheStore.GetString(isChildWarning
                ? "ChildAccountWarningLabel"
                : "InactiveAccountWarningLabel"
            );
        }

        return (
            <div className={popupClass}>
                {showFilePreview &&
                    this.renderFilePreview()
                }
                <div className="right portal-shared-item">
                    {!IsNullOrWhiteSpace(this.props.strategy.disclaimer) &&
                        <span>{this.props.strategy.disclaimer}</span>
                    }
                    {this.isPortalInvitationFlowV2Enabled && this.shouldShowInvitation && (
                        <ContactForm
                            email={this.state.email}
                            phone={this.state.phone}
                            emails={this.state.allContacts.emails}
                            phones={this.state.allContacts.phones}
                            onPhoneChange={phone => this.mutateState(s => {
                                s.phone = phone;
                            })}
                            onEmailChange={email => this.mutateState(s => {
                                s.email = email;
                            })}
                        />
                    )}
                    {this.props.strategy.shouldShowSubject && this.state.showSubjectReadOnly && (
                        <div className="shared-item-subject">
                            <h4>{cacheStore.GetString("SubjectLabel").toUpperCase()}</h4>
                            {IsNullOrWhiteSpace(this.state.subjectText) ? "-" : this.state.subjectText}
                        </div>
                    )}
                    {this.renderMessages()}
                    {this.renderMessageSectionAccordingToFeatureFlag(warningMsg, showFilePreview)}
                </div>
            </div>
        );
    }

    private renderMessages(): JSX.Element {
        const linkedMessages = this.state.linkedShareMessages?.sortBy(p => p.DateCreated).toArray();

        if (!linkedMessages?.length)
            return null;

        return (
            <div className="shared-item-messages">
                {linkedMessages.map(
                    message => {
                        const sentBy = message.IsFromPatient ?
                            NameHelper.formatFullName(message.PatientFirstName, message.PatientLastName, message.PatientPreferredName)
                            : (cacheStore.GetCliniciansPersonsByContactId(message.SentByContactId)?.NameWithPrefix ?? "");

                        const dateCreated = moment(message.DateCreated);
                        const sentDate = `${dateCreated.format("YYYY-MM-DD")} ${cacheStore.GetString("AtLabel")} ${dateCreated.format("hh:mm:ss A")}`;

                        const portalButtonProps = PortalHelper.getPortalButtonPropsForSyncStatus(
                            message.Status,
                            true
                        );

                        return (
                            <div className="flex-fullWidth-container bottom-border" key={"msg_" + message.tmpIdForUI}>
                                <div className="itemLeft itemNoShrink" style={{ width: "20px" }}>
                                    {this.props.isAsyncCallback && !message.IsFromPatient && (
                                        <CheckboxControl
                                            checked={message.IsSelected}
                                            item={message}
                                            onChange={() => {
                                                this.mutateState(s => s.linkedShareMessages = this.props.strategy.onPortalSharedItemMessageCheckChange(this.props.model, message));
                                            }}
                                        />
                                    )}

                                    {!this.props.isAsyncCallback && !message.IsFromPatient && (
                                        <CheckboxControl
                                            checked={message.IsSelected}
                                            item={message}
                                            operation={this.props.strategy.itemCheckOperation}
                                        />
                                    )}
                                </div>
                                <div className="itemGrow">
                                    <div className="flex-top-to-bottom-container">
                                        <div>
                                            <div>
                                                <span><strong>{sentBy}</strong> {sentDate}</span>
                                            </div>
                                            <div>
                                                <span>{JSXHelper.BreakStringIntoParagraphs(message.Text)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {!message.IsFromPatient && (
                                    <div className="itemNoGrow">
                                        <PortalStatusIcon icon="share" {...portalButtonProps} />
                                    </div>
                                )}
                            </div>
                        );
                    }
                )}
            </div>
        );
    }

    private renderLinkedItem(shouldShowFilePreview: boolean): JSX.Element {
        if (this.props.strategy.linkedItemType === PortalLinkedItemType.CommunicationHistory)
            return null;

        const sharedItem = this.props.model.get("SharedItem");
        const portalButtonProps = PortalHelper.getPortalButtonPropsForSyncStatus(
            PortalHelper.getLinkedItemPortalStatus(sharedItem),
            !!sharedItem?.IncludeLinkedItem,
            this.hasBeenModifiedSinceLastShared()
        );

        const linkedItem = this.props.model.get("LinkedItemModel");
        const linkedItemTypeHeader = linkedItem?.getItemTypeHeader() ?? "";
        const linkedItemTitle = linkedItem?.getItemTitle() ?? "";

        return (
            <div className="shared-item-linked-item">
                <div
                    className="flex-fullWidth-container flex-fullWidth-container-compact"
                    style={{ alignItems: "center" }}
                >
                    <div className="itemLeft itemNoShrink" style={{ width: "20px" }}>
                        <CheckboxControl
                            checked={this.state.includeLinkedItem}
                            disabled={!this.props.strategy.canCheckItem}
                            onChange={c => this.mutateState(s => s.includeLinkedItem = c)}
                        />
                    </div>
                    {this.props.strategy.itemIconClass && (
                        <div
                            className={`itemLeft ${this.props.strategy.itemIconClass}`}
                            style={{ minWidth: "45px" }}
                        />
                    )}
                    <div className="itemGrow">
                        <div>
                            <p className="bold" title={linkedItemTypeHeader}>
                                {linkedItemTypeHeader}
                            </p>
                            <p style={{ fontStyle: "oblique", wordBreak: "break-all" }} title={linkedItemTitle}>
                                {linkedItemTitle}
                            </p>
                        </div>
                    </div>
                    <div className="itemNoGrow">
                        <PortalStatusIcon icon="share" className="push-left-5" {...portalButtonProps} />
                    </div>
                </div>
                {shouldShowFilePreview && sharedItem?.Files?.length && (
                    <div className="flex-fullWidth-container flex-fullWidth-container-compact">
                        <div style={{ flexGrow: 0, flexShrink: 0, alignSelf: "flex-start", paddingLeft: "12px" }}>
                            {cacheStore.GetString("DocumentGridFilesLabel")}
                        </div>
                        <ul style={{ flexGrow: 1, marginLeft: "10px" }} className="portal-files">
                            {sharedItem.Files.map(file => (
                                <li key={file.FileUri} className={(file === this.state.selectedPreviewFile ? "bold" : "")}>
                                    <a onClick={() => this.onPreviewFileSelected(file)}>{file.FileName}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    private renderFilePreview(): JSX.Element {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const signableFile = signAndSendStrategy.signableFile;

        const displaySignaturesInPreviewMode =
            cacheStore.ClinicSettings.EnableQuickDocumentSigning
            && this.state.isPinValid
            && !this.state.employeeFormSign.isFirstTime
            && this.state.employeeFormSign.shouldSign;

        if (signableFile) {
            return (
                <div className="left portal-preview">
                    {signableFile instanceof ExportableFile
                        ? (
                            <FilePreviewComponentEditable
                                ref={ref => this.filePreviewComponentEditable = ref}
                                file={signableFile}
                                allowEditMode={this.state.isPinValid}
                                onPreviewModeChange={mode => this.mutateState(s => s.currentPreviewMode = mode)}
                                filePreviewMode={this.state.defaultFilePreviewComponentMode}
                                includeSignatureInPreviewMode={displaySignaturesInPreviewMode}
                            />
                        )
                        : (
                            <iframe
                                id="iframe-pdf-document"
                                src={signableFile}
                                height="100%"
                                width="100%"
                            />
                        )
                    }
                </div>
            );
        }

        if (!this.state.selectedPreviewFile)
            return <div className="left portal-preview" />;

        const fileUriWithSession = apiSettings
            .extendUrl(
                new URL(this.state.selectedPreviewFile.FileUri),
                { auth: true, params: { v: this.state.previewFileCachebust } }
            )
            .toString();

        if (this.state.selectedPreviewFile.MimeType.match(/^image\//i))
            return (
                <div className="left portal-preview" style={{ overflow: "auto" }}>
                    <img src={fileUriWithSession} />
                </div>
            );

        if (this.state.selectedPreviewFile.MimeType.match(/pdf/i))
            return (
                <iframe
                    id="iframe-pdf-document"
                    src={fileUriWithSession}
                    height="100%"
                    width="100%"
                />
            );

        return (
            <div className="left portal-preview">
                <object
                    style={{ width: "100%", height: "100%" }}
                    data={fileUriWithSession}
                    type={this.state.selectedPreviewFile.MimeType}
                >
                    <a href={fileUriWithSession} target="_blank">{this.state.selectedPreviewFile.FileName}</a>
                </object>
            </div>
        );
    }

    private renderMessageSectionAccordingToFeatureFlag(warningMsg: string, showFilePreview: boolean): JSX.Element {
        if (this.isBidirectionalCommunicationEnabled)
            return this.renderMessageSectionWithBidirectionalCommunication(warningMsg, showFilePreview);

        if (this.isPortalInvitationFlowV2Enabled)
            return this.renderMessageSectionWithNewInvitationFlow(warningMsg, showFilePreview);

        return this.renderMessageSection(warningMsg, showFilePreview);
    }

    private renderMessageSection(warningMsg: string, showFilePreview: boolean): JSX.Element {
        return (
            <>
                {this.renderLinkedItem(showFilePreview)}
                <div className="shared-item-new-message">
                    <h4>{cacheStore.GetString("NewMessageLabel").toUpperCase()}</h4>
                    {this.props.strategy.shouldShowSubject && !this.state.showSubjectReadOnly && (
                        <TextboxControl
                            maxLength={SharedItem.SubjectMaxLength}
                            className="portal-messages-new-message-input"
                            id={`${this.componentId}_subject`}
                            placeholder={cacheStore.GetString("SubjectLabel")}
                            onChange={subjectText => this.mutateState(s => s.subjectText = subjectText)}
                            value={this.state.subjectText}
                        />
                    )}
                    {!IsNullOrWhiteSpace(warningMsg) && (
                        <div className="red-text">
                            {warningMsg}
                        </div>
                    )}
                    {!IsNullOrWhiteSpace(this.props.model.get("ErrorMessage")) && (
                        <div className="red-text">
                            {this.props.model.get("ErrorMessage")}
                        </div>
                    )}
                    <TextAreaResizable
                        id={`${this.componentId}_txtArea`}
                        text={this.state.newMessageText}
                        isDisabled={!this.props.strategy.canEditMessage}
                        verticalResizeOnly
                        minHeightPx={100}
                        style={{ maxHeight: "150px", flexShrink: 0 }}
                        onChange={value => this.mutateState(s => s.newMessageText = value)}
                        debounceMs={0}
                    />
                    {this.state.isClickableTextPopupOpen && (
                        <div className="clickable-text-container">
                            <ClickableTextPanel
                                cssClass="text-helper-container sendtoportal-popup-clickable"
                                context="PORTAL"
                                inputElementId={`${this.componentId}_txtArea`}
                                onCloseEvent={() => this.closeClickableText()}
                                reduceHeight
                            />
                        </div>
                    )}
                    {this.props.strategy.canEditMessage && (
                        <div className="text-right">
                            <a onClick={() => { this.showClickableText(); }}>
                                {cacheStore.GetString("SelectFromListLabel")}
                            </a>
                        </div>
                    )}
                </div>
            </>
        );
    }

    private renderMessageSectionWithNewInvitationFlow(warningMsg: string, showFilePreview: boolean): JSX.Element {
        return (
            <div className="shared-item-new-message">
                <MessageForm
                    required
                    values={{
                        subject: this.state.subjectText,
                        body: this.state.newMessageText,
                        priority: null,
                        canPatientReplyWithMessage: null
                    }}
                    onChange={message => this.mutateState(s => {
                        s.subjectText = message.subject;
                        s.newMessageText = message.body;
                    })}
                    subHeading={(
                        <>
                            {!IsNullOrWhiteSpace(warningMsg) && (
                                <div className="red-text">
                                    {warningMsg}
                                </div>
                            )}
                            {!IsNullOrWhiteSpace(this.props.model.get("ErrorMessage")) && (
                                <div className="red-text">
                                    {this.props.model.get("ErrorMessage")}
                                </div>
                            )}
                        </>
                    )
                    }
                >
                    {this.renderLinkedItem(showFilePreview)}
                </MessageForm>
            </div>
        );
    }

    private renderMessageSectionWithBidirectionalCommunication(warningMsg: string, showFilePreview: boolean): JSX.Element {
        return (
            <>
                {this.renderLinkedItem(showFilePreview)}
                <PortalCommunicationMessageForm
                    componentId="SendToPortal_NewMessageForm"
                    sharedItem={this.props.model.get("SharedItem")}
                    actionType={SharedItemActionType.New}
                    containerClass="shared-item-new-message"
                    formContainerClass="portal-communication-form-new-message"
                    warningMessage={warningMsg}
                    hideSubjectField={this.state.showSubjectReadOnly}
                    isPopup
                    hideToField
                    onNewPortalCommunicationMessageChange={newFormState => this.props.onSetFormState({
                        ...this.props.formState,
                        ...newFormState
                    })}
                />
            </>
        );
    }

    private renderButtons(): JSX.Element {
        const sharedItem = this.props.model.get("SharedItem");

        const showRevokeAll = sharedItem
            && (sharedItem.Status === PortalSyncStatus.Pending
                || sharedItem.Status === PortalSyncStatus.Viewed
                || sharedItem.Status === PortalSyncStatus.Error);

        const sendAndPrintStrategy = this.props.strategy as ISendToPortalAndPrintPopupStrategy;

        const sendButtonIsDisabled = this.shouldSyncToPortalBeDisabled();

        return (
            <div className="flex-display flex-end">
                <ButtonGroup>
                    <ButtonControl
                        variant="neutral"
                        label={cacheStore.GetString("CancelLabel")}
                        onClick={() => this.props.onClosePopup ? this.props.onClosePopup() : this.props.strategy.onClosePopupClick()}
                    />
                    {showRevokeAll && (
                        <ButtonControl
                            variant="error"
                            label={cacheStore.GetString("RevokeAllLabel")}
                            onClick={async () => {
                                this.props.isAsyncCallback ?
                                    this.props.isAsyncCallback(await this.props.strategy.onRevokeClickAsync(this.props.model)) :
                                    this.props.strategy.onRevokeClick();
                            }}
                        />
                    )}
                    <ButtonControl
                        variant="brand"
                        label={this.isPortalInvitationFlowV2Enabled ? cacheStore.GetString("SendLabel") : this.props.strategy.sendToPortalLabel}
                        onClick={async () => {
                            if (this.state.currentPreviewMode === FilePreviewComponentMode.imageEdit)
                                this.filePreviewComponentEditable?.applyOverlaysAndSwitchToPreviewMode();

                            if (this.isPortalInvitationFlowV2Enabled) {
                                this.sendInvitation();
                            }

                            const { messageText, subjectText, canPatientReplyWithMessage, priority } = this.getNewMessageValues(this.state.subjectText, this.state.newMessageText);

                            this.props.isAsyncCallback ?
                                this.props.isAsyncCallback(await this.props.strategy.onSendToPortalClickAsync(
                                    subjectText,
                                    messageText,
                                    this.state.includeLinkedItem,
                                    this.props.model,
                                    priority,
                                    canPatientReplyWithMessage
                                ))
                                :
                                this.props.strategy.onSendToPortalClick(
                                    subjectText,
                                    messageText,
                                    this.state.includeLinkedItem,
                                    null,
                                    priority,
                                    canPatientReplyWithMessage
                                );

                            const userSettingPreferences = this.getExportableFileType();

                            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning && !this.getSignedPrintAuthorizations()) {
                                if (this.props.strategy.linkedItemType == PortalLinkedItemType.MedicalNoteForm)
                                    this.saveRadPdfFormAction();
                                else if (userSettingPreferences) {
                                    this.updateExportableFilePopupSetting();
                                }
                            }
                        }}
                        disabled={sendButtonIsDisabled}
                    />

                    {sendAndPrintStrategy?.onSendAndPrintClick && (
                        <ButtonControl
                            key="sendAndPrint"
                            variant="brand"
                            label={sendAndPrintStrategy.sendAndPrintLabel}
                            onClick={() => {
                                if (this.isPortalInvitationFlowV2Enabled) {
                                    this.sendInvitation();
                                }
                                sendAndPrintStrategy.onSendAndPrintClick(
                                    this.state.subjectText,
                                    this.state.newMessageText,
                                    this.state.includeLinkedItem
                                );
                            }}
                            disabled={sendButtonIsDisabled}
                        />
                    )}
                </ButtonGroup>
            </div>
        );
    }

    private showClickableText(): void {
        this.mutateState(s => s.isClickableTextPopupOpen = true);
    }

    private closeClickableText(): void {
        this.mutateState(s => s.isClickableTextPopupOpen = false);
    }

    private signatureIsRequired(): boolean {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;

        if (this.getSignedPrintAuthorizations())
            return false;

        return this.state.includeLinkedItem
            && signAndSendStrategy.isSignatureRequired
            && (!this.state.isPinValid);
    }

    private getNewMessageValues(subjectText: string, text: string):
    { subjectText: string, messageText: string, canPatientReplyWithMessage: boolean, priority: SharedItemPriority } {
        if (this.isBidirectionalCommunicationEnabled) {
            const { messageText, subject, canPatientReplyWithMessage, priority } = this.props.formState;

            return {
                subjectText: subject,
                messageText: messageText,
                canPatientReplyWithMessage: canPatientReplyWithMessage,
                priority: priority
            };
        }

        return {
            subjectText: subjectText,
            messageText: text,
            canPatientReplyWithMessage: false,
            priority: null
        };
    }

    private shouldSyncToPortalBeDisabled(): boolean {
        if (this.props.model.get("IsSending")
            || this.signatureIsRequired())
            return true;

        if (this.isPortalInvitationFlowV2Enabled && this.shouldShowInvitation) {
            const { email, phone } = this.state;
            const isValidEmail = email?.contactPointId && email?.address && EmailContact.validateEmail(email?.address);
            const isValidPhone = phone?.contactPointId && phone?.countryLookupId && phone?.phoneTypeId && phone?.number && PhoneNumberHelper.validate(phone?.number, phone?.countryLookupId);

            if (!(email || phone) || (email && !isValidEmail) || (phone && !isValidPhone)) {
                return true;
            }
        }

        const sharedItem = this.props.model.get("SharedItem");

        // always allow re-sending for failed shared items
        if (sharedItem?.Status === PortalSyncStatus.Error)
            return false;

        const currentlyShared = sharedItem
            && (sharedItem.Status === PortalSyncStatus.Pending
                || sharedItem.Status === PortalSyncStatus.Viewed);

        let linkedItemHasValue = false;
        let subjectHasValue = false;

        const subjectText = this.isBidirectionalCommunicationEnabled ? this.props.formState.subject : this.state.subjectText;
        const messageText = this.isBidirectionalCommunicationEnabled ? this.props.formState.messageText : this.state.newMessageText;

        if (currentlyShared) {
            linkedItemHasValue = this.state.includeLinkedItem !== sharedItem.IncludeLinkedItem;

            subjectHasValue = !this.state.showSubjectReadOnly
                && subjectText !== this.getDefaultSubject()
                && !IsNullOrWhiteSpace(subjectText);
        }
        else {
            linkedItemHasValue = this.state.includeLinkedItem;

            subjectHasValue = !this.state.showSubjectReadOnly
                && !IsNullOrWhiteSpace(subjectText);
        }

        return !this.haveShareItemMessagesChanged()
            && !this.hasBeenModifiedSinceLastShared()
            && !linkedItemHasValue
            && !subjectHasValue
            && IsNullOrWhiteSpace(messageText);
    }

    private getDefaultValueForIncludeLinkItem(): boolean {
        const linkedItemType = this.props.strategy.linkedItemType;

        if (linkedItemType === PortalLinkedItemType.CommunicationHistory)
            return false;

        const sharedItem = this.props.model.get("SharedItem");

        return !sharedItem
            || (sharedItem.IncludeLinkedItem && sharedItem.Status !== PortalSyncStatus.Revoked);
    }

    private getDefaultSubject(): string {
        return this.props.model.get("SharedItem")?.Subject || this.props.model.get("DefaultSubject");
    }

    private haveShareItemMessagesChanged(): boolean {
        const changedMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(
            this.props.model
        );

        return !!changedMessages?.length;
    }

    private hasBeenModifiedSinceLastShared(): boolean {
        return this.props.model.get("LinkedItemModel").hasBeenModifiedSinceLastShared();
    }

    private onPreviewFileSelected(file: SharedItemFile): void {
        this.mutateState(
            s => {
                s.selectedPreviewFile = file;
                s.previewFileCachebust = Date.now();
            }
        );
    }

    private applySignatureOnPinSuccess(pin: string = null): void {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const signableFileIsExportable = signAndSendStrategy.signableFile instanceof ExportableFile;

        this.mutateState(
            s => {
                if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                    if (this.props.strategy.linkedItemType == PortalLinkedItemType.MedicalNoteForm) {
                        const exportableFile = signAndSendStrategy.signableFile as ExportableFile;
                        const pinFrequencyModel = LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency);
                        const pinFrequencyToken = sharedItemProviderFactory.get(this.props.strategy.linkedItemType).getPinFrequencyToken(pinFrequencyModel);
                        exportableFile.applyOverlayElement(new PrepositionedSignatureOverlayElement({ Pin: "", Token: pinFrequencyToken }));
                        s.employeeFormSign.shouldSign = true;
                    }
                    else if (this.props.strategy.linkedItemType == PortalLinkedItemType.LabRequest || this.props.strategy.linkedItemType == PortalLinkedItemType.ConsultationRequest) {
                        this.onUserSettingSignaturePreferenceChange(true);
                    }
                }
            },
            async () => {
                if (!this.state.isPinValid) {
                    const pinFrequencyOption = sharedItemProviderFactory.get(this.props.strategy.linkedItemType).getPinFrequencyDocumentType();
                    if (pinFrequencyOption != null) {
                        const response = await SignatureClient.refreshPinFrequencyToken(pinFrequencyOption, pin);
                        if (response) {
                            sessionStore.dispatch(LocalOperations.Login.UpdatePinFrequency, {
                                pinFrequencyDocumentType: response.SignaturePinType,
                                token: response.Token
                            });

                            sessionStore.dispatch(LocalOperations.Settings.UpdatePinFrequency, {
                                pinFrequencyDocumentType: response.SignaturePinType,
                                token: response.Token
                            });
                        }
                    }
                }

                this.mutateState(s => {
                    s.isPinValid = true;
                    s.displayPinComponent = false;

                    if (!signableFileIsExportable) {
                        return;
                    }

                    s.defaultFilePreviewComponentMode = FilePreviewComponentMode.imageEdit;
                    s.currentPreviewMode = FilePreviewComponentMode.imageEdit;
                });

                signAndSendStrategy.onSignatureAccepted?.();
            }
        );
    }

    private onPinReject(): void {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const signableFileIsExportable = signAndSendStrategy.signableFile instanceof ExportableFile;

        this.mutateState(
            s => {
                s.isPinValid = false;

                if (!signableFileIsExportable)
                    return;

                s.defaultFilePreviewComponentMode = FilePreviewComponentMode.iFramePreview;
                s.currentPreviewMode = FilePreviewComponentMode.iFramePreview;
            },
            () => signAndSendStrategy.onSignatureRejected?.()
        );
    }

    private hasBeenSigned(): boolean {
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const signableFileIsExportable = signAndSendStrategy.signableFile instanceof ExportableFile;
        if (!signableFileIsExportable)
            return false;

        return signAndSendStrategy.signableFile.hasSignature()
            || (
                this.props.strategy.linkedItemType == PortalLinkedItemType.MedicalNoteForm
                && !this.state.employeeFormSign.isFirstTime
                && this.state.employeeFormSign.shouldSign
            );
    }

    private async saveRadPdfFormAction(): Promise<boolean> {
        if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
            return false;
        }

        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        const exportableFile = signAndSendStrategy.signableFile as ExportableFile;
        const formId = (exportableFile as FileTypeRadPdf).formId ?? (signAndSendStrategy.signableFile as FileTypeAttachment).radPdfFormId;

        return await CustomFaxClient.saveEmployeeFormSign({
            EmployeeId: sessionStore.getCurrentEmployee()?.ContactId,
            FormId: formId,
            ActionType: ExportMode.SendToPortal,
            ShouldSign: this.filePreviewComponentEditable.getCurrentOverlay().length > 0
        });
    }

    /**
     * Get exportable file plan action type
     */
    private getExportableFileType(): "examRequest" | "consultationRequest" {
        switch (this.props.strategy.linkedItemType) {
            case PortalLinkedItemType.LabRequest:
                return "examRequest";

            case PortalLinkedItemType.ConsultationRequest:
                return "consultationRequest";

            default:
                return null;
        }
    }

    /**
     * return SignedPrintAuthorizations from current document
     */
    private getSignedPrintAuthorizations(): SignedPrintAuthorization | null {
        if (!cacheStore.ClinicSettings.EnableQuickDocumentSigning)
            return null;

        const linkItemType = this.props.strategy.linkedItemType;

        if (linkItemType === PortalLinkedItemType.LabRequest || linkItemType === PortalLinkedItemType.ConsultationRequest) {
            const model = this.props.model.get("LinkedItemModel");
            return SignedPrintAuthorization.getLast(model?.SignedPrintAuthorizations);
        }

        return null;
    }

    /**
     * Updating user preference plan actions popup setting for send to portal
     */
    private updateExportableFilePopupSetting(): void {
        sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettingsExportableFilePopup, {
            settings: this.state.exportableFilePopupSetting
        });
    }

    /**
     * Remove user signature for plan actions exportable file
     */
    private onRemoveSignatureButtonClick(): void {
        this.onUserSettingSignaturePreferenceChange(false);
        const signAndSendStrategy = this.props.strategy as ISignAndSendToPortalPopupStrategy;
        signAndSendStrategy.onRemoveSignature();
    }

    /**
     * Change shouldSign state if user sign or remove signature
     * @param {boolean} shouldSign
     */
    private onUserSettingSignaturePreferenceChange(shouldSign: boolean): void {
        this.mutateState(s => {
            s.employeeFormSign.shouldSign = shouldSign;
            const exportableFileType = this.getExportableFileType();
            let userSettings = s.exportableFilePopupSetting[exportableFileType]?.exportableFileSignatureSettings;

            if (userSettings) {
                const signatureUserSetting = userSettings?.filter(p => p.actionType === ExportMode.SendToPortal);
                if (signatureUserSetting && signatureUserSetting.length > 0)
                    signatureUserSetting[0].shouldSign = shouldSign;
                else {
                    userSettings.push(new ExportableFileSignatureSettingsModel({
                        actionType: ExportMode.SendToPortal,
                        shouldSign: shouldSign
                    }));
                }
            }
            else {
                s.exportableFilePopupSetting[exportableFileType].exportableFileSignatureSettings = [new ExportableFileSignatureSettingsModel({
                    actionType: ExportMode.SendToPortal,
                    shouldSign: shouldSign
                })];
            }
        });
    }

    /**
     * get if user need to sign document according his previous action
     * @returns {boolean}
     */
    private hasSignatureAccordingToPlanActionsPreferences(): boolean {
        const exportableFileType = this.getExportableFileType();

        if (!exportableFileType)
            return !!exportableFileType;

        const settings = this.state.exportableFilePopupSetting[exportableFileType]?.exportableFileSignatureSettings;
        return settings?.firstOrDefault(p => p.actionType === ExportMode.SendToPortal)?.shouldSign === true;
    }

    private async loadPatient(): Promise<Person> {
        const patientId = this.props.model.get("PatientContactId");

        const cachedPatient = patientStore.PatientModels[patientId];
        if (cachedPatient) {
            return Person.FromPatient(cachedPatient);
        }

        const response = await PatientApiClient.loadPatientInfo(patientId, false, false);
        if (!response.isSuccess) {
            return null;
        }

        const fetchedPatient = Patient.FromResponseData(response.data);
        return Person.FromPatient(fetchedPatient);
    }

    private getPatientContacts(patient: Person): { emails: IInvitationContactEmail[], phones: IInvitationContactPhone[] } {
        const emails: IInvitationContactEmail[] = patient?.Emails
            .filter(email => email.NotificationsEnabled)
            .map(email => ({
                address: email.Address,
                contactPointId: email.ContactPointTypeLookupId
            })) ?? [];

        const phones: IInvitationContactPhone[] = patient?.Phones
            .filter(email => email.NotificationsEnabled)
            .map(phone => ({
                countryLookupId: phone.CountryLookupId,
                contactPointId: phone.ContactPointTypeLookupId,
                phoneTypeId: phone.PhoneTypeLookupId,
                number: phone.Number
            })) ?? [];

        return { emails, phones };
    }

    private async sendInvitation(): Promise<void> {
        const { email, phone } = this.state;

        const contactPointId = email?.contactPointId ?? phone?.contactPointId;

        if (!contactPointId)
            return;

        const invitationContact: InvitationContact = (email || phone) ? {
            contactPointTypeLookupId: contactPointId,
            emailAddress: this.state.email ? email.address : null,
            phoneNumber: phone ? {
                phoneTypeLookupId: phone.phoneTypeId,
                countryLookupId: phone.countryLookupId,
                number: phone.number
            } : null
        } : null;

        const data: SendInvitationRequest = {
            contactId: this.props.model.get("PatientContactId"),
            invitationContact,
            mandatoryFieldsData: null,
            invitationMessage: null
        };

        const response = await sendInvitation(data);

        if (response.isSuccess) {
            sessionStore.dispatchAsync(
                LocalOperations.Portal.Portal_UpdateState,
                {
                    patientContactId: this.props.model.get("PatientContactId"),
                    portalStatus: PortalRegistrationState.RegistrationPending
                }
            );
        }
        else {
            sessionStore.showPopup(response.errors);
        }
    }
}

const EnhancedSendToPortalPopupComponent = withPortalCommunicationMessageForm(SendToPortalPopup);

export { EnhancedSendToPortalPopupComponent as SendToPortalPopup };
